/**
 * pages/OnboardingGuide.jsx
 * Static step-by-step onboarding guide for customer's OCI administrator.
 * Accessible without login — share URL directly with client.
 */
import { useState } from "react";

const STEPS = [
  {
    id: 1,
    title: "Create a billing reader user",
    subtitle: "This is a read-only user — it cannot make any changes to your OCI resources.",
    warning: null,
    info: "✓ This user will only read billing data. It cannot create, modify or delete any resources.",
    infoType: "success",
    content: `OCI Console → Identity & Security → Users → Create User

Name:         beyondata-billing-reader
Description:  BeyonData Cost Intelligence - read only billing access
Email:        (leave blank or use your IT team email)

→ Click Create`,
  },
  {
    id: 2,
    title: "Create a group and add the user",
    subtitle: "Groups are used to manage permissions for multiple users at once.",
    warning: null,
    info: null,
    content: `OCI Console → Identity & Security → Groups → Create Group

Name:         beyondata-billing-readers
Description:  BeyonData billing platform read access

→ Click Create
→ Click on the group → Members → Add Member
→ Select: beyondata-billing-reader → Add`,
  },
  {
    id: 3,
    title: "Create IAM policy",
    subtitle: "This allows the platform to read your cost and usage data.",
    warning: "⚠ Must be created at ROOT compartment level — not inside any sub-compartment. Check the top of the page shows your tenancy name, not a compartment name.",
    info: null,
    content: `OCI Console → Identity & Security → Policies → Create Policy

Name:         beyondata-billing-read-policy
Description:  Allow BeyonData platform to read cost and usage reports
Compartment:  (root — your tenancy name)

Add these 3 statements exactly as shown:

  Allow group beyondata-billing-readers to read usage-report in tenancy
  Allow group beyondata-billing-readers to inspect usage-report in tenancy
  Allow group beyondata-billing-readers to read cost-reports in tenancy

→ Click Create`,
  },
  {
    id: 4,
    title: "Generate API key",
    subtitle: "The API key allows the platform to securely authenticate with your OCI tenancy.",
    warning: "✗ The private key (.pem file) can only be downloaded once. Save it immediately before closing the dialog.",
    warningType: "danger",
    info: null,
    content: `OCI Console → Identity & Security → Users
→ Click: beyondata-billing-reader
→ Left menu: API Keys → Add API Key

Select: Generate API Key Pair
→ Download Private Key (.pem file) — SAVE THIS FILE
→ Click: Add

A configuration preview appears. Copy the entire snippet:

  [DEFAULT]
  user=ocid1.user.oc1..aaaaaaaXXXXXXXXXXXXXXXX
  fingerprint=ab:cd:ef:12:34:56:78:90:ab:cd:ef:12
  tenancy=ocid1.tenancy.oc1..aaaaaaaXXXXXXXXXXXX
  region=ap-mumbai-1
  key_file=~/.oci/oci_api_key.pem`,
  },
  {
    id: 5,
    title: "Get tenancy details",
    subtitle: "Basic information about your OCI tenancy needed for the platform setup.",
    warning: null,
    info: null,
    content: `OCI Console → Administration → Tenancy Details

Copy and share with BeyonData:

  Tenancy Name:  _______________________________
  Tenancy OCID:  ocid1.tenancy.oc1..______________
  Home Region:   _______________________________
  
  Primary region used:   ______________________
  (e.g. ap-mumbai-1)
  
  DR region (if any):    ______________________
  (e.g. ap-hyderabad-1)
  
  DR in separate tenancy?   Yes / No
  (If Yes, repeat Steps 1-4 for the DR tenancy)`,
  },
  {
    id: 6,
    title: "List your compartments",
    subtitle: "Skip this step if all your resources are in the root compartment.",
    warning: null,
    info: "✓ If you are not sure about compartments, share the naming pattern and BeyonData will identify them during setup.",
    infoType: "success",
    content: `OCI Console → Identity & Security → Compartments

For each compartment used for this project:

  Name:    cmp-company-prod-app
  OCID:    ocid1.compartment.oc1.._______
  Purpose: Production App / Production DB / DR App / Network / Security

Note the naming convention used e.g.:
  cmp-clientname-p-projectname-app  (Primary app)
  cmp-clientname-p-projectname-db   (Primary database)
  cmp-clientname-dr-projectname-app (DR app)
  
Contract details:
  Contract start date:  ______________________
  Contract end date:    ______________________
  Client display name:  ______________________
  (Name to appear on reports e.g. "ABC Corporation Pvt Ltd")`,
  },
  {
    id: 7,
    title: "Set up email alerts (optional but recommended)",
    subtitle: "Receive email notifications when costs exceed budget thresholds.",
    warning: null,
    info: "✓ After creating the subscription, you must confirm it by clicking the link in the confirmation email. Status must show Active (not Pending).",
    infoType: "success",
    content: `OCI Console → Application Integration → Notifications
→ Topics → Create Topic

  Name:        beyondata-cost-alerts
  Description: Cost threshold alerts from BeyonData platform

→ Click Create → Click on the topic
→ Subscriptions → Create Subscription
  Protocol:  Email
  Email:     (address that should receive alerts)
→ Check inbox → Click CONFIRM SUBSCRIPTION link

→ Copy Topic OCID:
  ocid1.onstopic.oc1.ap-mumbai-1._____________

Also add this policy at root compartment:

OCI Console → Identity & Security → Policies → Create Policy

  Name:      beyondata-notifications-policy
  Statement: Allow any-user to use ons-topic in tenancy
             where request.permission='ONS_TOPIC_PUBLISH'

Alert recipient emails (comma separated):
  _____________________________________________`,
  },
  {
    id: 8,
    title: "Share with BeyonData",
    subtitle: "Once you have all the above, share the following with the BeyonData team.",
    warning: "Share the .pem private key file via a secure channel — encrypted email, secure file transfer, or password manager share link.",
    warningType: "danger",
    info: null,
    content: null,
    checklist: [
      "Config snippet (user OCID, fingerprint, tenancy OCID, region) — from Step 4",
      "Private key .pem file — from Step 4",
      "Tenancy name and OCID — from Step 5",
      "Primary and DR region names — from Step 5",
      "Compartment names and OCIDs — from Step 6 (if applicable)",
      "Contract start and end dates — from Step 6",
      "Client display name for reports — from Step 6",
      "Notifications topic OCID — from Step 7 (if alerts needed)",
      "Alert recipient email addresses — from Step 7",
    ],
  },
];

const Code = ({ children }) => (
  <pre style={{
    background:"#F8FAFC",
    border:"1px solid #E2E8F0",
    borderRadius:8,
    padding:"14px 16px",
    fontSize:12,
    fontFamily:"monospace",
    lineHeight:1.7,
    color:"#1E293B",
    whiteSpace:"pre-wrap",
    wordBreak:"break-word",
    margin:0,
  }}>
    {children}
  </pre>
);

const Alert = ({ type, children }) => {
  const styles = {
    success: { bg:"#F0FDF4", border:"#BBF7D0", color:"#166534" },
    warning: { bg:"#FFFBEB", border:"#FDE68A", color:"#92400E" },
    danger:  { bg:"#FEF2F2", border:"#FECACA", color:"#991B1B" },
    info:    { bg:"#EFF6FF", border:"#BFDBFE", color:"#1E40AF" },
  };
  const s = styles[type] || styles.info;
  return (
    <div style={{
      background:s.bg, border:`1px solid ${s.border}`,
      borderRadius:8, padding:"10px 14px",
      fontSize:13, color:s.color, lineHeight:1.5,
    }}>
      {children}
    </div>
  );
};

export default function OnboardingGuide() {
  const [current, setCurrent] = useState(0);
  const [checked, setChecked] = useState({});
  const step = STEPS[current];

  const toggleCheck = (idx) => {
    setChecked(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const allChecked = step.checklist
    ? step.checklist.every((_, i) => checked[i])
    : true;

  return (
    <div style={{
      minHeight:"100vh",
      background:"#F8FAFC",
      fontFamily:"'DM Sans','Segoe UI',sans-serif",
      padding:"32px 16px",
    }}>
      <div style={{ maxWidth:760, margin:"0 auto" }}>

        {/* Header */}
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#64748B", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:6 }}>
            BeyonData Solutions Private Limited
          </div>
          <div style={{ fontSize:26, fontWeight:800, color:"#0F172A", marginBottom:6 }}>
            OCI Cost Intelligence Platform
          </div>
          <div style={{ fontSize:15, color:"#64748B" }}>
            Customer onboarding guide — OCI setup checklist
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom:24 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
            <span style={{ fontSize:12, color:"#64748B" }}>Step {current + 1} of {STEPS.length}</span>
            <span style={{ fontSize:12, color:"#64748B" }}>{Math.round(((current + 1) / STEPS.length) * 100)}% complete</span>
          </div>
          <div style={{ height:6, background:"#E2E8F0", borderRadius:3, overflow:"hidden" }}>
            <div style={{
              height:"100%",
              width:`${((current + 1) / STEPS.length) * 100}%`,
              background:"#2563EB",
              borderRadius:3,
              transition:"width 0.3s ease",
            }}/>
          </div>
        </div>

        {/* Step tabs */}
        <div style={{ display:"flex", gap:4, marginBottom:20, flexWrap:"wrap" }}>
          {STEPS.map((s, i) => (
            <button key={s.id} onClick={() => setCurrent(i)} style={{
              padding:"5px 12px", borderRadius:20, border:"1px solid",
              fontSize:12, fontWeight:600, cursor:"pointer",
              transition:"all 0.15s",
              borderColor: i === current ? "#2563EB" : "#E2E8F0",
              background:  i === current ? "#2563EB" : i < current ? "#F0FDF4" : "#fff",
              color:       i === current ? "#fff" : i < current ? "#16A34A" : "#64748B",
            }}>
              {i < current ? "✓ " : ""}{s.id}. {s.title.split(" ").slice(0, 3).join(" ")}…
            </button>
          ))}
        </div>

        {/* Main card */}
        <div style={{
          background:"#fff",
          borderRadius:12,
          border:"1px solid #E2E8F0",
          overflow:"hidden",
          marginBottom:16,
        }}>
          {/* Card header */}
          <div style={{ padding:"20px 24px", borderBottom:"1px solid #F1F5F9" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:6 }}>
              <div style={{
                width:32, height:32, borderRadius:"50%",
                background:"#EFF6FF", color:"#2563EB",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:14, fontWeight:700, flexShrink:0,
              }}>
                {current < STEPS.length - 1 ? step.id : "✓"}
              </div>
              <div style={{ fontSize:17, fontWeight:700, color:"#0F172A" }}>{step.title}</div>
            </div>
            {step.subtitle && (
              <div style={{ fontSize:13, color:"#64748B", paddingLeft:44 }}>{step.subtitle}</div>
            )}
          </div>

          {/* Card body */}
          <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:14 }}>
            {step.warning && (
              <Alert type={step.warningType || "warning"}>{step.warning}</Alert>
            )}

            {step.content && <Code>{step.content}</Code>}

            {step.checklist && (
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {step.checklist.map((item, i) => (
                  <label key={i} style={{
                    display:"flex", alignItems:"flex-start", gap:10,
                    padding:"10px 14px",
                    background: checked[i] ? "#F0FDF4" : "#F8FAFC",
                    border: `1px solid ${checked[i] ? "#BBF7D0" : "#E2E8F0"}`,
                    borderRadius:8, cursor:"pointer", transition:"all 0.15s",
                  }}>
                    <input
                      type="checkbox"
                      checked={!!checked[i]}
                      onChange={() => toggleCheck(i)}
                      style={{ marginTop:2, flexShrink:0, width:16, height:16 }}
                    />
                    <span style={{
                      fontSize:13,
                      color: checked[i] ? "#166534" : "#374151",
                      textDecoration: checked[i] ? "line-through" : "none",
                      lineHeight:1.5,
                    }}>
                      {item}
                    </span>
                  </label>
                ))}
                {allChecked && (
                  <Alert type="success">
                    ✓ All items checked — you are ready to share with BeyonData team.
                    Contact: <strong>support@beyondatagroup.com</strong>
                  </Alert>
                )}
              </div>
            )}

            {step.info && (
              <Alert type={step.infoType || "info"}>{step.info}</Alert>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <button
            onClick={() => setCurrent(p => Math.max(0, p - 1))}
            disabled={current === 0}
            style={{
              padding:"9px 20px", borderRadius:8, border:"1px solid #E2E8F0",
              background: current === 0 ? "#F8FAFC" : "#fff",
              color: current === 0 ? "#CBD5E1" : "#374151",
              fontSize:13, fontWeight:600, cursor: current === 0 ? "default" : "pointer",
            }}>
            ← Previous
          </button>

          <div style={{ fontSize:12, color:"#94A3B8" }}>
            {current + 1} / {STEPS.length}
          </div>

          <button
            onClick={() => {
              if (current < STEPS.length - 1) setCurrent(p => p + 1);
            }}
            disabled={current === STEPS.length - 1 && !allChecked}
            style={{
              padding:"9px 20px", borderRadius:8,
              border: current === STEPS.length - 1 ? "1px solid #BBF7D0" : "1px solid #BFDBFE",
              background: current === STEPS.length - 1
                ? (allChecked ? "#F0FDF4" : "#F8FAFC")
                : "#EFF6FF",
              color: current === STEPS.length - 1
                ? (allChecked ? "#166534" : "#CBD5E1")
                : "#1D4ED8",
              fontSize:13, fontWeight:600,
              cursor: current === STEPS.length - 1 && !allChecked ? "default" : "pointer",
            }}>
            {current === STEPS.length - 1
              ? (allChecked ? "✓ All done!" : "Complete checklist above")
              : "Next →"}
          </button>
        </div>

        {/* Footer */}
        <div style={{ textAlign:"center", marginTop:32, fontSize:12, color:"#94A3B8" }}>
          BeyonData Solutions Private Limited · support@beyondatagroup.com
          <br/>OCI Cost Intelligence Platform v3.0
        </div>

      </div>
    </div>
  );
}
